import { type ReactNode, useId } from 'react'
import {
  KibTableCell,
  KibTableContainer,
  KibTableHeading,
  KibTableRow,
} from '@chewy/kib-table-new-react'
import classNames from 'classnames'

import { OrderStatusChip } from '~/components/dashboard/clients/timeline/prescriptions/rxTracker/components/OrderStatusChip'
import i18n from '~/locales/i18n'

import type { OrderTableCardHeader, OrderTableRow } from '../orderTableTypes'
import {
  formatRxTrackerText,
  getChewyOrderInfoLabels,
  getPrescriptionFromRow,
} from '../rxCardUtilities'

import styles from './OrderTable.module.scss'

export type { OrderTableRow } from '../orderTableTypes'

export interface OrderTableProps {
  cardHeaders?: OrderTableCardHeader[]
  className?: string
  getRowKey?: (rowIndex: number, row: OrderTableRow[]) => string
  headers: string[]
  renderRowActions?: (row: OrderTableRow[]) => ReactNode
  rows: OrderTableRow[][]
  subHeader?: string | null
}

const emptyCell: Readonly<OrderTableRow> = {
  label: '',
  relativeTime: null,
  value: '',
}

const generateRowKey = (
  componentId: string,
  cells: OrderTableRow[],
): string => {
  const prescription = getPrescriptionFromRow(cells)
  const prescriptionId = prescription?.id ?? ''
  return `${componentId}-${prescriptionId}`
}

const getColumnTitle = (header: string, cellIndex: number): string =>
  header.trim() || `column-${cellIndex}`

/** Maps column index → width class; drug-expiration only when `dataColumnCount === 5`. */
const getOrderTableDataColumnWidthClass = (
  cellIndex: number,
  dataColumnCount: number,
): string | undefined => {
  if (dataColumnCount === 5 && cellIndex === 3) {
    return styles.orderTableColDrugExpiration
  }
  switch (cellIndex) {
    case 0:
      return styles.orderTableColDate
    case 1:
      return styles.orderTableColQty
    case 2:
      return styles.orderTableColFill
    default:
      return undefined
  }
}

const getInlineLabel = (
  cell: OrderTableRow,
  columnTitle: string,
): string | null => {
  const trimmed = cell.label?.trim() ?? ''
  if (!trimmed || trimmed === columnTitle) {
    return null
  }
  return trimmed
}

const renderChewyOrderNumbers = (
  cardHeader: OrderTableCardHeader | undefined,
  row: OrderTableRow[],
): ReactNode => {
  const showOrderAndPrescriptionNumbers =
    cardHeader?.showOrderAndPrescriptionNumbers ?? false
  const prescription = getPrescriptionFromRow(row)

  if (!showOrderAndPrescriptionNumbers || !prescription) {
    return null
  }

  const { orderNumber: orderLabel, prescriptionNumber: rxLabel } =
    getChewyOrderInfoLabels(prescription)

  return (
    <span className={styles.cardHeaderOrderInfo}>
      <span className={styles.cardHeaderOrderNumberGroup}>
        {orderLabel}: {cardHeader?.orderNumber ?? ''}
      </span>
      <span className={styles.cardHeaderPrescriptionNumberGroup}>
        {rxLabel}: {cardHeader?.rxNo ?? ''}
      </span>
    </span>
  )
}

const renderTableHeaderRow = (
  headers: string[],
  rowKey: string,
  row: OrderTableRow[],
  renderRowActionsProp: OrderTableProps['renderRowActions'],
) => (
  <KibTableRow>
    {headers.map((header, cellIndex) => {
      const columnTitle = getColumnTitle(header, cellIndex)
      const isLastDataColumn = cellIndex === headers.length - 1
      const widthClass = getOrderTableDataColumnWidthClass(
        cellIndex,
        headers.length,
      )

      return (
        <KibTableHeading
          className={classNames(
            styles.columnHeading,
            widthClass,
            isLastDataColumn && styles.orderTableLastDataColumn,
          )}
          key={`${rowKey}-th-${columnTitle}`}
          scope="col"
        >
          {columnTitle}
        </KibTableHeading>
      )
    })}
    {renderRowActionsProp ? (
      <KibTableHeading
        aria-label={i18n.t('Common:ACTIONS')}
        className={styles.actionsColumn}
        scope="col"
      >
        <div data-click-exclude="true">{renderRowActionsProp(row)}</div>
      </KibTableHeading>
    ) : null}
  </KibTableRow>
)

const renderTableCell = (
  cell: OrderTableRow,
  columnTitle: string,
  isLastDataColumn: boolean,
  isOrderDateColumn: boolean,
) => {
  const inlineLabel = getInlineLabel(cell, columnTitle)
  const displayValue = formatRxTrackerText(cell.value, columnTitle)

  const displayInlineLabel = inlineLabel
    ? formatRxTrackerText(inlineLabel)
    : null

  const displaySecondaryText = cell.secondaryText?.trim()
    ? formatRxTrackerText(cell.secondaryText.trim(), columnTitle)
    : null

  return (
    <div className={styles.cardField}>
      {displayInlineLabel ? (
        <span className={styles.cardFieldLabel}>{displayInlineLabel}</span>
      ) : null}
      <div className={styles.cardFieldValue}>
        <span
          className={classNames(
            styles.cardFieldText,
            isOrderDateColumn && styles.cardFieldTextOrderDatePrimary,
          )}
          title={
            isLastDataColumn || isOrderDateColumn
              ? displayValue || undefined
              : undefined
          }
        >
          {displayValue}
        </span>
        {cell.relativeTime && (
          <span className={styles.cardFieldDateLabel}>{cell.relativeTime}</span>
        )}
        {displaySecondaryText ? (
          <span
            className={
              cell.useSecondaryTextStyle
                ? styles.cardFieldDateLabel
                : styles.cardFieldSecondaryText
            }
            title={displaySecondaryText ?? undefined}
          >
            {displaySecondaryText}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export const OrderTable = ({
  cardHeaders,
  headers,
  rows,
  renderRowActions,
  getRowKey,
  subHeader,
  className,
}: OrderTableProps) => {
  const componentId = useId()

  if (!rows?.length || !headers?.length) {
    return null
  }

  return (
    <div className={classNames(styles.cardsContainer, className)}>
      {rows.map((row, rowIndex) => {
        const rowKey = getRowKey
          ? getRowKey(rowIndex, row)
          : generateRowKey(componentId, row)

        const cardHeader = cardHeaders?.[rowIndex]
        const cardHeaderText =
          cardHeader?.text ?? subHeader ?? i18n.t('Common:ORDER')
        const statusTag = cardHeader?.status

        return (
          <div className={styles.orderCard} key={rowKey}>
            <div className={styles.cardHeader}>
              <div className={styles.cardHeaderTitleRow}>
                <span className={styles.cardHeaderText}>{cardHeaderText}</span>
                {renderChewyOrderNumbers(cardHeader, row)}
              </div>
              {statusTag && (
                <div className={styles.cardHeaderActions}>
                  <OrderStatusChip
                    className={styles.statusTag}
                    status={statusTag}
                  />
                </div>
              )}
            </div>

            <KibTableContainer
              aria-label={i18n.t('Common:ORDER_DETAILS')}
              caption={null}
              className={styles.orderCardTable}
              header={renderTableHeaderRow(
                headers,
                rowKey,
                row,
                renderRowActions,
              )}
              spacing="condensed"
            >
              <KibTableRow>
                {headers.map((header, cellIndex) => {
                  const cell = row[cellIndex] ?? emptyCell
                  const columnTitle = getColumnTitle(header, cellIndex)
                  const isLastDataColumn = cellIndex === headers.length - 1
                  const widthClass = getOrderTableDataColumnWidthClass(
                    cellIndex,
                    headers.length,
                  )

                  return (
                    <KibTableCell
                      className={classNames(
                        styles.dataCell,
                        widthClass,
                        isLastDataColumn && styles.orderTableLastDataColumn,
                      )}
                      key={`${rowKey}-td-${columnTitle}`}
                    >
                      {renderTableCell(
                        cell,
                        columnTitle,
                        isLastDataColumn,
                        cellIndex === 0,
                      )}
                    </KibTableCell>
                  )
                })}
                {renderRowActions ? (
                  <KibTableCell
                    aria-hidden
                    className={classNames(
                      styles.dataCell,
                      styles.actionsColumn,
                    )}
                  />
                ) : null}
              </KibTableRow>
            </KibTableContainer>
          </div>
        )
      })}
    </div>
  )
}
